import _ from 'lodash';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import toast from 'react-hot-toast';

// Queries
import { DELETE_USER, GET_CURR_USER, REGISTER_USER, SET_ACTIVE_COMPANY, UPDATE_USER } from './user.queries';
import { useCallback, useMemo, useRef } from 'react';
import { IUser } from '../../../lib/types';
import { useLogout } from '../auth/auth.service';
import { ICurrUserResults, ISetActiveCompanyProps, IUpdateUserParams } from './user.types';
import { logError } from '../../../lib/utils';
import { wipeCurrCompanyCache } from '../companies/company.utils';
import { CURR_COMPANY_REFETCH_QUERIES, USER_COMPANIES_REFETCH_QUERIES } from '../companies/company.const';
import { activeCompanyLoadingVar } from '../companies/company.service';

export const useFetchCurrUser = () => {
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<ICurrUserResults>(GET_CURR_USER, {
    fetchPolicy: 'cache-and-network'
  });

  // Memoize computed values
  const user = useMemo(() => _.get(data, 'loggedInUser'), [data]);
  const isGlobalAdmin = useMemo(() => user?.isAdmin, [user?.isAdmin]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    user,
    isGlobalAdmin
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, user, isGlobalAdmin]);
};

export const useRegisterUser = ({
  authId,
  joiningExistingCompany
}: {
  authId?: string;
  joiningExistingCompany?: boolean;
}) => {
  const [mutation, rest] = useMutation<{ register: IUser }>(REGISTER_USER, {
    context: { serializationKey: 'MUTATION', tracked: true, skip: !authId },
    update: (cache, { data }) => {
      // Update logged in user query with newly created user
      if (data?.register)
        cache.writeQuery({ query: GET_CURR_USER, data: { loggedInUser: data.register }, overwrite: true });
    },
    refetchQueries: USER_COMPANIES_REFETCH_QUERIES,
    // Only await refetch queries if we know that user will be guaranteed to have existing company.
    // Otherwise the refetch queries will fail and the user register will be considered a failure on the client side.
    awaitRefetchQueries: !!joiningExistingCompany
  });

  const registerUser = useCallback(
    (inviteCodeId: string) => {
      activeCompanyLoadingVar(true);
      return mutation({ variables: { authId, inviteCodeId, createPersonalCompany: false } }).finally(() =>
        activeCompanyLoadingVar(false)
      );
    },
    [authId, mutation]
  );

  return { registerUser, ...rest };
};

export const useArchiveUser = () => {
  const { handleLogout, loading } = useLogout();
  const [mutation, rest] = useMutation(DELETE_USER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    onCompleted: () => handleLogout(),
    onError: (err) => {
      const errMsg = 'Unable to archive user';
      logError(errMsg, (err as Error).message);
      toast.error(errMsg);
    }
  });

  return { archiveUser: mutation, ...rest, loading: rest.loading || loading };
};

export const useSetActiveCompany = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const mutationSucceededRef = useRef(false);

  const [mutation, rest] = useMutation<ISetActiveCompanyProps>(SET_ACTIVE_COMPANY, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      // Update curr user
      if (data?.setActiveCompany) {
        mutationSucceededRef.current = true;
        wipeCurrCompanyCache(cache);
        cache.writeQuery({ query: GET_CURR_USER, data: { loggedInUser: data.setActiveCompany }, overwrite: true });
      }
    },
    onCompleted: () => {
      toast.success('Successfully switched organization');
    },
    onError: (err) => {
      // Only called if the mutation itself fails (not refetch queries)
      const errMsg = 'Unable to switch organization';
      logError(errMsg, (err as Error).message);
      toast.error(errMsg);
      console.error('Failed to switch organization:', err);
    },
    awaitRefetchQueries: true,
    refetchQueries: CURR_COMPANY_REFETCH_QUERIES
  });

  const setActiveCompany = useCallback(
    async (companyId: string) => {
      activeCompanyLoadingVar(true);
      mutationSucceededRef.current = false;

      try {
        const result = await mutation({ variables: { companyId } });
        return result;
      } catch (err) {
        // If mutation succeeded but refetch failed, don't show error (already showed success)
        if (mutationSucceededRef.current) {
          console.warn('Organization switched successfully, but some data refetch queries failed:', err);
        }
        // If mutation itself failed, onError already showed toast, just re-throw
        throw err;
      } finally {
        activeCompanyLoadingVar(false);
      }
    },
    [mutation]
  );

  return { ...rest, setActiveCompany, loading: activeCompanyLoading };
};

export const useUpdateUser = () => {
  const [mutation, rest] = useMutation<{ updateUser: IUser }, IUpdateUserParams>(UPDATE_USER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data?.updateUser)
        cache.writeQuery({ query: GET_CURR_USER, data: { loggedInUser: data.updateUser }, overwrite: true });
    }
  });

  const updateUser = useCallback((variables: IUpdateUserParams) => mutation({ variables }), [mutation]);

  return { updateUser, ...rest };
};
